import { cn } from "@/lib/utils";
import { ClipboardList, Package, Users, Droplets, Search, AlertTriangle, CheckCircle2, XCircle, Trash2, PackagePlus, PackageCheck, PlayCircle, PauseCircle, PenLine, Bug } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Notification } from "@/hooks/useNotifications";
import { rolDeAviso, rutaDeAviso } from "@/lib/notificationRoutes";
import { Button } from "@/components/ui/button";
import { useNotificationTranslation } from "@/hooks/useNotificationTranslation";
import { useAuth } from "@/contexts/AuthContext";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  getRelativeTime: (date: string | null) => string;
}

/**
 * Icono + color de acento por tipo de aviso. `color` es un hex, no clases de
 * Tailwind: la pastilla de la app lleva un halo del propio color
 * (`0 6px 14px <color>42`) que hay que componer en tiempo de ejecución, y así el
 * mapa vale igual para el desplegable y para la tarjeta de la página — antes
 * NotificationCard mantenía a mano un segundo mapa «espejo» de estos tonos.
 *
 * La escala es la de la app: verde #16A34A (algo salió bien), azul #2B7DE0
 * (informativo), ámbar #E0962E (requiere atención), naranja #E8712F (el aviso
 * más serio del grupo).
 */
export const typeConfig: Record<string, { icon: React.ComponentType<{ className?: string }>, color: string }> = {
  task_created: { icon: ClipboardList, color: "#16A34A" },
  task_assigned: { icon: ClipboardList, color: "#2B7DE0" },
  task_updated: { icon: PenLine, color: "#2B7DE0" },
  task_started: { icon: PlayCircle, color: "#2B7DE0" },
  task_stopped: { icon: PauseCircle, color: "#E0962E" },
  task_due_soon: { icon: AlertTriangle, color: "#E0962E" },
  task_completed: { icon: CheckCircle2, color: "#16A34A" },
  task_cancelled: { icon: XCircle, color: "#E0962E" },
  low_stock: { icon: Package, color: "#E0962E" },
  team_member_added: { icon: Users, color: "#16A34A" },
  treatment_logged: { icon: Droplets, color: "#2B7DE0" },
  irm_warning: { icon: AlertTriangle, color: "#E8712F" },
  product_request: { icon: PackagePlus, color: "#2B7DE0" },
  product_request_resolved: { icon: PackageCheck, color: "#16A34A" },
  pest_focus_new: { icon: Bug, color: "#E8712F" },
  pest_focus_worse: { icon: Bug, color: "#D64545" },
};

/** Gris de los tipos que aún no tienen entrada propia arriba. */
export const NOTIFICATION_FALLBACK = { icon: AlertTriangle, color: "#64748B" };

export function getNotificationVisual(type: string | null | undefined) {
  return typeConfig[type || ""] ?? NOTIFICATION_FALLBACK;
}

/**
 * Pastilla de icono del lenguaje visual de la app: relleno macizo del acento,
 * glifo blanco, halo del propio color y filo de luz arriba. El halo depende del
 * color, así que va en `style` y no en clases.
 */
export function notificationTileStyle(color: string): React.CSSProperties {
  return {
    backgroundColor: color,
    boxShadow: `0 6px 14px ${color}42, inset 0 1px 0 rgba(255,255,255,0.28)`,
  };
}

// Fallback routes when data.route is missing
// Build a deep-link route from IDs already present in notification.data.
// No SQL changes: the target pages auto-open the item from these query params
// (Planner reads ?taskId=, SprayLogs reads ?logId=, InventoryDetailPage reads
// ?action=entry, Inventory reads ?requestId=).
/**
 * @param isWorker el trabajador no tiene planificador: su tarea vive en
 *   `/worker/tasks/:id`. Sin esto se le mandaba a `/planner`, una ruta que su rol no
 *   puede pintar, así que el aviso de «tarea asignada» no llevaba a ninguna parte.
 */
/**
 * UNA SOLA TABLA DE DESTINOS (02-09-2026). Esto tenía su propia copia de las
 * reglas y la función que manda el push tenía otra, y ya habían divergido: el
 * push llevaba los partes de monitoreo a la ficha de la sesión y esto no conocía
 * ese caso. El mismo aviso abría dos sitios según por dónde se tocara.
 *
 * Las reglas viven ahora en `rutasDeAviso` (copia de cliente de
 * `supabase/functions/_shared/notificationRoute.ts`, que el edge function no
 * puede importar desde `src/`), y `notificationRoute.parity.test.ts` compara las
 * dos y falla si alguien toca una sin la otra.
 */
export function resolveNotificationRoute(
  notification: Notification,
  /** El rol de la sesión (o, como antes, `true` si es trabajador). */
  role: string | boolean | null = false,
): string | undefined {
  const rol = typeof role === "boolean" ? (role ? "worker" : "other") : rolDeAviso(role);
  return rutaDeAviso({
    type: notification.type,
    data: (notification.data as Record<string, unknown> | null) ?? {},
    rol,
  });
}

export function NotificationItem({ notification, onMarkAsRead, onDelete, getRelativeTime }: NotificationItemProps) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { translateNotification } = useNotificationTranslation();
  const config = getNotificationVisual(notification.type);
  const Icon = config.icon;

  // Get translated title and message
  const translated = translateNotification(notification);

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    const route = resolveNotificationRoute(notification, role);
    if (route) {
      navigate(route);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "w-full flex items-start gap-3 p-3 text-left rounded-2xl border cursor-pointer group transition-all",
        "hover:shadow-[0_4px_14px_rgba(20,60,100,0.09)]",
        // Sin leer: fondo azulado y filete marcado. Leído: blanco liso y un punto
        // menos de contraste, para que la vista se vaya sola a lo que falta por ver.
        notification.read
          ? "bg-white border-[#E4EDF5]"
          : "bg-[#F5FAFF] border-[#B6DDED]",
      )}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0"
        style={notificationTileStyle(config.color)}
      >
        <Icon className="w-[18px] h-[18px]" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            "text-[13.5px] leading-snug truncate",
            notification.read ? "font-semibold text-foreground/90" : "font-bold text-foreground",
          )}>
            {translated.title}
          </p>
          {/* El punto de «sin leer» viaja junto a la hora: es donde se lee el
              estado, y deja de empujar al botón de borrar de sitio según la fila. */}
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
              {getRelativeTime(notification.created_at)}
            </span>
            {!notification.read && (
              <span className="w-2 h-2 rounded-full bg-[#2B7DE0]" aria-hidden />
            )}
          </span>
        </div>
        <p className="text-[12px] leading-relaxed text-muted-foreground line-clamp-2 mt-0.5">
          {translated.message}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 rounded-lg text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10"
        onClick={handleDelete}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
